import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Box,
  FormControl,
  Button,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Circle as CircleIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ServiceStatusAggregation } from "../../Models/Types"; // Adjust path as needed
import "./KSPMonitorView.scss";

// --- Configuration: Tag Definitions ---
// Define the logic for associating applications with tags here.
// Keywords are case-insensitive substrings to match against the application name.
// The order of this array determines the primary sorting order of the applications.
const TAG_DEFINITIONS = [
  {
    id: "infra",
    label: "Infrastrutture",
    color: "#d32f2f", // Red
    keywords: ["infra", "network", "server", "db", "database"],
  },
  {
    id: "cross",
    label: "Piattaforme Trasversali",
    color: "#2e7d32", // Green
    keywords: ["identity", "api", "yucca", "ai", "sso", "gateway"],
  },
  {
    id: "app_platform",
    label: "Piattaforme Applicative",
    color: "#fbc02d", // Yellow (Darker for readability)
    keywords: ["posta", "acta", "cosmo", "moon", "crm"],
  },
  {
    id: "key_app",
    label: "Key Applications",
    color: "#1976d2", // Blue
    keywords: ["dma", "sap", "erp"],
  },
];

// Fallback for apps that don't match any tag
const DEFAULT_TAG = {
  id: "other",
  label: "Altre Applicazioni",
  color: "#9e9e9e", // Grey
};

// --- Helper Functions ---
const getTagForApplication = (appTags: string[] = []) => {
  // Strictly check explicit tags from the API
  for (const tag of TAG_DEFINITIONS) {
    if (
      appTags.some((apiTag) => apiTag.toLowerCase() === tag.label.toLowerCase())
    ) {
      return tag;
    }
  }

  // If no explicit tag matches, default to "Altre Applicazioni"
  return DEFAULT_TAG;
};

const formatDuration = (seconds: number | string): string => {
  const secNum = typeof seconds === "string" ? parseFloat(seconds) : seconds;
  if (isNaN(secNum)) return "00:00:00";
  const h = Math.floor(secNum / 3600);
  const m = Math.floor((secNum % 3600) / 60);
  return `${h}h ${m}m`;
};

// --- Main Component ---

interface KSPMonitorViewProps {
  aggregations: ServiceStatusAggregation[];
}

const KSPMonitorView: React.FC<KSPMonitorViewProps> = ({ aggregations }) => {
  console.log("Aggregations", aggregations);
  (window as any)["aggregations"] = aggregations;
  const [filterTag, setFilterTag] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | "ok" | "ko">("all");
  const theme = useTheme();
  // isMobile is still used inside the component for layout logic if needed
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 1. Process Data: Assign Tags and Calculate Stats
  const processedData = useMemo(() => {
    const taggedApps = aggregations.map((app) => {
      // Extract all tags from the inner items and remove duplicates
      const allTags = Array.from(
        new Set(app.items.flatMap((item) => item.application_tags || [])),
      );

      return {
        ...app,
        tag: getTagForApplication(allTags),
      };
    });
    // Calculate KPI Stats per Tag
    const stats = [
      ...TAG_DEFINITIONS,
      // DEFAULT_TAG // Commented out to hide the "Altre Applicazioni" tile. Uncomment to restore.
    ].map((tagDef) => {
      const appsInTag = taggedApps.filter((a) => a.tag.id === tagDef.id);
      return {
        ...tagDef,
        total: appsInTag.length,
        ok: appsInTag.filter((a) => a.aggregate_status).length,
        ko: appsInTag.filter((a) => !a.aggregate_status).length,
      };
    });

    return { taggedApps, stats };
  }, [aggregations]);

  // 2. Filter Data based on UI selection and apply Sorting
  const filteredApps = useMemo(() => {
    const filtered = processedData.taggedApps.filter((app) => {
      // Hide "Altre Applicazioni" from the results list entirely.
      if (app.tag.id === "other") return false;

      const matchesTag = filterTag === "all" || app.tag.id === filterTag;
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "ok" ? app.aggregate_status : !app.aggregate_status);
      return matchesTag && matchesStatus;
    });

    // Sort the resulting array
    return filtered.sort((a, b) => {
      // Primary sort: Order by KSP Category index from TAG_DEFINITIONS
      const orderA = TAG_DEFINITIONS.findIndex((t) => t.id === a.tag.id);
      const orderB = TAG_DEFINITIONS.findIndex((t) => t.id === b.tag.id);

      // Handle default/other tags (push to end)
      const safeOrderA = orderA === -1 ? 999 : orderA;
      const safeOrderB = orderB === -1 ? 999 : orderB;

      if (safeOrderA !== safeOrderB) {
        return safeOrderA - safeOrderB;
      }

      // Secondary sort: Alphabetical by Application Name
      return a.application.localeCompare(b.application);
    });
  }, [processedData.taggedApps, filterTag, filterStatus]);

  return (
    <Box>
      <Typography className="section-title">Key Solution Portfolio</Typography>

      <Box sx={{ p: 2 }}>
        {/* --- FILTERS TOOLBAR --- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 2,
            bgcolor: "#fff",
            p: 1,
            borderRadius: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtra per Categoria</InputLabel>
            <Select
              value={filterTag}
              label="Filtra per Categoria"
              onChange={(e) => setFilterTag(e.target.value)}
            >
              <MenuItem value="all">Tutte le categorie</MenuItem>
              {TAG_DEFINITIONS.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        bgcolor: t.color,
                        borderRadius: "50%",
                      }}
                    />
                    {t.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", gap: { xs: 1, sm: 0 } }}
          >
            <Chip
              label="Tutti"
              onClick={() => setFilterStatus("all")}
              variant={filterStatus === "all" ? "filled" : "outlined"}
              color="primary"
            />
            <Chip
              label="Solo OK"
              onClick={() => setFilterStatus("ok")}
              variant={filterStatus === "ok" ? "filled" : "outlined"}
              color="success"
            />
            <Chip
              label="Solo KO"
              onClick={() => setFilterStatus("ko")}
              variant={filterStatus === "ko" ? "filled" : "outlined"}
              color="error"
            />
          </Stack>
        </Box>

        {/* --- KPI CARDS --- */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {processedData.stats.map((stat) => {
            // Hide "Other" category from KPI cards if empty
            if (stat.id === "other" && stat.total === 0) return null;

            const isSelected = filterTag === stat.id;
            const isAnotherSelected = filterTag !== "all" && !isSelected;

            // Hide other cards if one is selected to save vertical space
            if (isAnotherSelected) return null;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stat.id}>
                <Card
                  sx={{
                    borderLeft: `6px solid ${stat.color}`,
                    boxShadow: isSelected ? 4 : 1,
                    transform: isSelected ? "scale(1.02)" : "scale(1)",
                    transition: "all 0.2s",
                    cursor: "pointer",
                  }}
                >
                  <CardActionArea
                    onClick={() => setFilterTag(isSelected ? "all" : stat.id)}
                    sx={{ height: "100%", p: 1 }}
                  >
                    <CardContent sx={{ p: "8px !important" }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          fontWeight: "bold",
                          wordBreak: "break-word", // Prevent title overflow
                        }}
                      >
                        {stat.label}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" }, // Stack in column on small screens
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          mt: 1,
                          gap: { xs: 1, sm: 0 },
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                          {stat.total}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ flexWrap: "wrap" }}
                        >
                          <Chip
                            size="small"
                            icon={<CheckCircleIcon />}
                            label={stat.ok}
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            icon={<CancelIcon />}
                            label={stat.ko}
                            color="error"
                            variant={stat.ko > 0 ? "filled" : "outlined"}
                          />
                        </Stack>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* --- APP LIST --- */}
        <Box>
          {filteredApps.map((agg) => (
            <Accordion
              key={agg.application_id}
              sx={{
                mb: 1,
                borderLeft: `6px solid ${agg.tag.color}`, // Visual association logic
                "&:before": { display: "none" }, // Remove default divider
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // Stacks elements vertically on mobile
                    alignItems: { xs: "flex-start", sm: "center" },
                    width: "100%",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pr: 2,
                  }}
                >
                  {/* Left: App Name */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1em",
                        wordBreak: "break-word", // Protects from very long string overflows
                      }}
                    >
                      {agg.application}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agg.items.length} monitor associati
                    </Typography>
                  </Box>

                  {/* Right/Bottom container for Tags and Status */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 1, sm: 2 },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => {
                        const link = `https://www.symon.it/dashboard/applications/${agg.application_id}/summary?timeframe=7200`;
                        console.log("Navigating to: " + link);
                        window.open(link, "_blank", "noopener,noreferrer");
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Vai al dettaglio
                    </Button>

                    {/* Tag Label - Fixed width on desktop to ensure strict alignment */}
                    <Box
                      sx={{
                        width: { sm: 210 },
                        display: "flex",
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                      }}
                    >
                      <Chip
                        label={agg.tag.label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: `${agg.tag.color}20`,
                          color: agg.tag.color,
                          border: `1px solid ${agg.tag.color}`,
                        }}
                      />
                    </Box>

                    {/* Status Box - Fixed width */}
                    <Box
                      sx={{
                        width: { sm: 70 },
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: agg.aggregate_status ? "green" : "red",
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {agg.koMonitors === 0 ? "OK" : `${agg.koMonitors} KO`}
                      </Typography>
                    </Box>

                    {/* Duration Box - Fixed width */}
                    <Box
                      sx={{
                        width: { sm: 90 },
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {agg.aggregate_status
                          ? formatDuration(agg.minDuration)
                          : formatDuration(agg.maxDuration)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails
                sx={{ bgcolor: "#f9fafb", borderTop: "1px solid #eee" }}
              >
                {/* Reuse list logic from original component, simplified */}
                <List dense disablePadding>
                  {agg.items.map((item) => (
                    <ListItem key={item.monitor_service_id} divider>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CircleIcon
                          sx={{
                            fontSize: 12,
                            color: item.run_status ? "green" : "red",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.monitor_name}
                        secondary={`Durata: ${formatDuration(item.duration_secs)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}

          {filteredApps.length === 0 && (
            <Typography align="center" sx={{ mt: 4, color: "text.secondary" }}>
              Nessuna applicazione trovata con i filtri correnti.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default KSPMonitorView;
